import {
  ColumnWidthOutlined,
  DeleteFilled,
  DeleteOutlined,
  DisconnectOutlined,
  DownloadOutlined,
  EditOutlined,
  LinkOutlined,
  LoadingOutlined,
  LockFilled,
  PhoneOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { useMarkets } from '@api/useMarkets'
import { useSites } from '@api/useSites'
import { useStateSelect } from '@components/Admin/Site/useStateSelect'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import {
  addCommasToNumber,
  BBDTag,
  DataItem,
  EditSaveButton,
  ErrorNotification,
  GraviButton,
  Horizontal,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import api from '@utils/api'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Button, Col, Form, Popconfirm, Row, Select, Switch, Tooltip } from 'antd'
import moment from 'moment'
import React, { createRef, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { AutoCompleteInput } from '../../../../_SiteWide/Form/AutoCompleteInput'
import { DatePickerInput } from '../../../../_SiteWide/Form/DatePickerInput'
import { DropdownGroupSelect } from '../../../../_SiteWide/Form/DropdownGroupSelect'
import { DropdownSelect } from '../../../../_SiteWide/Form/DropdownSelect'
import { FormInput } from '../../../../_SiteWide/Form/FormInput'
import { useMasterMetadata } from '@api/useMetadata/useMasterMetadata'
import { useRateManagement } from '@api/useFreight/useFreight'

const { Option } = Select

export function Info({ store, sectors, counterparties, monitorStrategies }) {
  const { labeledStateOptions, onStateSearch } = useStateSelect()
  const [partnerSitesOpen, setPartnerSitesOpen] = useState(false)
  const { scopes } = useSecurityContext()
  const [editing, setEditing] = useState(false)
  const [isSaveLoading, setIsSaveLoading] = useState(false)
  const [form] = Form.useForm()
  const [trailerConfigOptions, setTrailerConfigOptions] = useState([])
  const [showEllipsis, setShowEllipsis] = useState(false)
  const queryClient = useQueryClient()
  const { useMarketMetadataQuery } = useMarkets()
  const { data: marketMetadata } = useMarketMetadataQuery()
  const { updateSiteMutation, useSplitPartnersQuery } = useSites()
  const { mutateAsync, reset: resetMutation } = updateSiteMutation()
  const payload = useMemo(() => (editing ? store?.id : null), [store, editing])
  const { data: splitPartners } = useSplitPartnersQuery(payload)
  const partners = useMemo(
    () => store?.split_partners?.map((partner) => ({ ...partner, store_number: partner.site_number })),
    [store?.split_partners]
  )

  // Add freight regions specific query using useMasterMetadata
  const { data: freightMetadata, isLoading: isLoadingFreightMetadata } = useMasterMetadata('ratebook_options')

  // Use the freightMetadata for regions
  const freightRegions = useMemo(
    () =>
      freightMetadata?.freight_regions?.map((region) => ({
        label: region.name,
        value: region.id,
      })) || [],
    [freightMetadata?.freight_regions]
  )

  const { formatDistance } = useFormatter()
  const formattedDistance = formatDistance(null, false)
  const textBoxRef = useRef(null)

  const wrappedFields = [
    'state',
    'sector',
    'monitor_type',
    'drop_workflow',
    'counterparty',
    'supply_owner',
    'freight_customer',
  ]
  const [wrapperRefs, setWrapperRefs] = useState([])
  useEffect(() => {
    setWrapperRefs(wrappedFields.reduce((acc, field) => ({ ...acc, [field]: createRef() }), {}))
  }, [])

  useEffect(() => {
    setTrailerConfigOptions(marketMetadata?.trailer_configs || [])
  }, [marketMetadata])

  const canEditStores = scopes.admin?.site_write
  const canEditCreditHold = scopes.admin?.store_credit_hold_write
  const onCompHold = useMemo(() => store?.is_out_of_compliance, [store?.is_out_of_compliance])
  const handleSave = async (formData) => {
    setIsSaveLoading(true)
    if (formData.open_date) formData.open_date = moment(formData.open_date).toISOString()
    if (formData.close_date) formData.close_date = moment(formData.close_date).toISOString()
    if (formData.compliance_hold_date)
      formData.compliance_hold_date = moment(formData.compliance_hold_date).format('YYYY-MM-DDT00:00:00')
    const { stateEdit } = formData
    if (stateEdit !== undefined) {
      formData.state = formData.stateEdit
    }

    if (formData?.trailer_config === 'None') {
      formData.trailer_config = null
    }

    if (formData.counterparty) {
      const cp = counterparties.find((cp) => cp.name === formData.counterparty)
      formData.counterparty_name = cp.name
      formData.counterparty_id = cp.id
    } else {
      delete store.counterparty_name
      delete store.counterparty_id
    }

    if (formData.supply_owner) {
      const cp = counterparties.find((cp) => cp.name === formData.supply_owner)
      formData.supply_owner_name = cp.name
      formData.supply_owner_id = cp.id
    } else {
      delete store.supply_owner_name
      delete store.supply_owner_id
    }

    if (formData.freight_customer) {
      const cp = counterparties.find((cp) => cp.name === formData.freight_customer)
      formData.freight_customer_name = cp.name
      formData.freight_customer_id = cp.id
    } else {
      delete store.freight_customer_name
      delete store.freight_customer_id
    }

    // Handle freight region (now we receive the ID directly)
    if (formData.freight_region_id) {
      // Find the region object by ID to get the name
      const region = freightMetadata?.freight_regions?.find((r) => r.id === formData.freight_region_id)
      if (region) {
        // Keep the ID and set the name
        formData.freight_region_name = region.name
      } else {
        // If region not found, clear both
        formData.freight_region_id = null
        formData.freight_region_name = null
      }
    } else {
      // Clear both fields if nothing selected
      formData.freight_region_id = null
      formData.freight_region_name = null
    }
    if (formData.split_partner_site_ids) {
      formData.split_partner_site_ids = formData.split_partner_site_ids.map((sp) => sp.value)
    }

    const updatedStore = { ...store, ...formData, id: store.id }
    try {
      await mutateAsync(updatedStore)
    } finally {
      setIsSaveLoading(false)
      setEditing(false)
    }
  }

  useEffect(() => {
    // Also set the freight_region_id to display the name
    if (store?.freight_region_id && freightMetadata?.freight_regions) {
      const region = freightMetadata.freight_regions.find((r) => r.id === store.freight_region_id)
      if (region) {
        form.setFieldsValue({
          freight_region_id: region.name,
        })
      }
    }
  }, [store, freightMetadata])

  useEffect(() => {
    if (editing && store) {
      form.setFieldsValue({
        ...store,
        stateEdit: store.state,
      })

      // freight_region_id should already be the ID, no need to convert
    }
  }, [editing, store, form, freightMetadata])

  const handleDelete = async () => {
    await api.deleteStore({ id: store.id }).then(() => {
      queryClient.invalidateQueries({
        queryKey: ['siteOverviewQuery'],
      })
      setEditing(false)
    })
  }

  return (
    <div className='store-expand-card p-4'>
      <Form
        form={form}
        className='store-info-form'
        size='small'
        onFinish={handleSave}
        onFinishFailed={(msg) => {
          ErrorNotification(msg)
          setIsSaveLoading(false)
        }}
        layout='vertical'
        initialValues={{
          ...store,
          counterparty: store.counterparty_name,
          supply_owner: store.supply_owner_name,
          freight_customer: store.freight_customer_name,
          freight_region_id: store.freight_region_name,
          split_partner_site_ids: store.split_partners?.map((sp) => ({ label: sp.site_number, value: sp.site_id })),
        }}
      >
        <StoreNameNumberRow
          wrapperRefs={wrapperRefs}
          store={store}
          canEditStores={canEditStores}
          editing={editing}
          setEditing={setEditing}
          handleDelete={handleDelete}
          saveLoading={isSaveLoading}
          counterparties={counterparties}
          monitorStrategies={monitorStrategies}
          canEditCreditHold={canEditCreditHold}
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '1rem',
          }}
        >
          <DataItem label='Sector'>
            <DropdownGroupSelect
              options={sectors && sectors}
              initialValue={`${store.market} - ${store.sector}`}
              editable={editing}
              name='sector'
              required={false}
              placeholder='Sector'
            />
          </DataItem>

          <DataItem label='Site Address'>
            <FormInput name='address' placeholder='Address' editable={editing} initialValue={store.address} />
          </DataItem>

          <DataItem label='UST Expiration Date'>
            <Horizontal verticalCenter>
              {!editing && !store?.compliance_hold_date ? (
                <Texto>--/--/---</Texto>
              ) : (
                <DatePickerInput
                  name='compliance_hold_date'
                  placeholder='---/---/---'
                  editable={editing}
                  initialValue={store.compliance_hold_date ? formatters.DATE(store.compliance_hold_date) : null}
                  format={formatters.DATE}
                />
              )}
              {onCompHold && (
                <Texto className='ml-2' appearance='error'>
                  <WarningOutlined /> UST Hold
                </Texto>
              )}
            </Horizontal>
          </DataItem>

          <DataItem label='Open Date'>
            <DatePickerInput
              name='open_date'
              placeholder='Open Date'
              editable={editing}
              initialValue={store.open_date ? formatters.DATE(moment.utc(store.open_date).local()) : null}
              format={formatters.DATE}
            />
          </DataItem>

          <DataItem label='Site Hours'>
            <FormInput name='hours' placeholder='9 AM - 5 PM' editable={editing} initialValue={store.hours} />
          </DataItem>

          <DataItem label='Contact Number'>
            <FormInput
              editable={editing}
              name='phone_number'
              placeholder='Contact Number'
              initialValue={store.phone_number}
            />
          </DataItem>
          <Horizontal>
            <DataItem label='City' extraClass='mr-3'>
              <FormInput name='city' placeholder='City' editable={editing} initialValue={store.city} />
            </DataItem>

            <div ref={wrapperRefs.state}>
              <DataItem label='State'>
                <AutoCompleteInput
                  name='stateEdit'
                  editable={editing}
                  initialValue={store.state}
                  placeholder='Search for State'
                  options={labeledStateOptions}
                  onSearch={onStateSearch}
                  allowClear
                  style={{ width: 60 }}
                  getPopupContainer={(_) => wrapperRefs.state?.current}
                />
              </DataItem>
            </div>

            <DataItem label='Postal Code' extraClass='ml-3'>
              <FormInput
                name='postal_code'
                placeholder='Postal Code'
                editable={editing}
                initialValue={store.postal_code}
              />
            </DataItem>
          </Horizontal>

          <DataItem label='Close Date'>
            <DatePickerInput
              name='close_date'
              placeholder='Close Date'
              editable={editing}
              initialValue={store.close_date ? formatters.DATE(moment.utc(store.close_date).local()) : null}
              format={formatters.DATE}
            />
          </DataItem>
          <DataItem label='Trailer Config'>
            <DropdownSelect
              editable={editing}
              initialValue={store.trailer_config ?? 'None'}
              name='trailer_config'
              options={[...trailerConfigOptions, 'None']}
            />
          </DataItem>
          {editing ? (
            <div ref={wrapperRefs.counterparty}>
              <DataItem label='Counterparty'>
                <Form.Item name='counterparty' rules={[{ required: true, message: 'Counterparty is required' }]}>
                  <Select
                    getPopupContainer={(_) => wrapperRefs.counterparty?.current}
                    allowClear
                    name='counterparty'
                    placeholder='Counterparty'
                    showSearch
                    style={{ width: '100%' }}
                  >
                    {counterparties?.map((counterparty, i) => (
                      <Option key={i} value={counterparty.name}>
                        {counterparty.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </DataItem>
            </div>
          ) : (
            <div className='vertical-flex-center flex-1'>
              <Texto className='mr-4' appearance='medium' weight='bold'>
                Site Counterparty
              </Texto>
              <Texto className='mr-4'>{store.counterparty_name}</Texto>
            </div>
          )}
          {editing ? (
            <div ref={wrapperRefs.supply_owner}>
              <DataItem label='Supply Owner'>
                <Form.Item name='supply_owner' rules={[{ required: true, message: 'Supply owner is required' }]}>
                  <Select
                    getPopupContainer={(_) => wrapperRefs.supply_owner?.current}
                    allowClear
                    name='supply_owner'
                    placeholder='Supply Owner'
                    showSearch
                    style={{ width: '100%' }}
                  >
                    {counterparties?.map((counterparty, i) => (
                      <Option key={i} value={counterparty.name}>
                        {counterparty.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </DataItem>
            </div>
          ) : (
            <div className='vertical-flex-center flex-1'>
              <Texto className='mr-4' appearance='medium' weight='bold'>
                Supply Owner
              </Texto>
              <Texto className='mr-4'>{store.supply_owner_name}</Texto>
            </div>
          )}
          {editing ? (
            <div ref={wrapperRefs.freight_customer}>
              <DataItem label='Freight Customer'>
                <Form.Item
                  name='freight_customer'
                  rules={[{ required: true, message: 'Freight customer is required' }]}
                >
                  <Select
                    getPopupContainer={(_) => wrapperRefs.freight_customer?.current}
                    allowClear
                    name='freight_customer'
                    placeholder='Freight Customer'
                    showSearch
                    style={{ width: '100%' }}
                  >
                    {counterparties?.map((counterparty, i) => (
                      <Option key={i} value={counterparty.name}>
                        {counterparty.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </DataItem>
            </div>
          ) : (
            <div className='vertical-flex-center flex-1'>
              <Texto className='mr-4' appearance='medium' weight='bold'>
                Freight Customer
              </Texto>
              <Texto className='mr-4'>{store.freight_customer_name}</Texto>
            </div>
          )}
          {editing ? (
            <DataItem label='Freight Region'>
              <Form.Item name='freight_region_id' rules={[{ required: false }]}>
                <Select
                  placeholder='Select Freight Region'
                  allowClear
                  loading={isLoadingFreightMetadata}
                  options={freightRegions}
                  disabled={!editing}
                />
              </Form.Item>
            </DataItem>
          ) : (
            <div className='vertical-flex-center flex-1'>
              <Texto className='mr-4' appearance='medium' weight='bold'>
                Freight Region
              </Texto>
              <Texto className='mr-4'>{store.freight_region_name || 'None'}</Texto>
            </div>
          )}
        </div>
        <div>
          {editing ? (
            <DataItem label='Split Partners'>
              <Form.Item name='split_partner_site_ids' rules={[{ required: false }]}>
                <Select
                  open={partnerSitesOpen}
                  onBlur={() => setPartnerSitesOpen(false)}
                  onFocus={() => setPartnerSitesOpen(true)}
                  loading={!splitPartners}
                  allowClear
                  placeholder='Split Partners'
                  showSearch
                  style={{ width: '100%' }}
                  mode='multiple'
                  labelInValue
                  options={splitPartners?.map((sp) => ({
                    label: sp.store_number,
                    value: sp.id,
                    key: sp.id,
                    distance: sp.distance,
                    split_me: sp.split_me,
                  }))}
                  optionRender={(option) => (
                    <Horizontal verticalCenter justifyContent='space-between'>
                      <Texto style={{ flex: 1 }}>{option.label}</Texto>
                      <Horizontal verticalCenter flex={1}>
                        <ColumnWidthOutlined className='mr-2' />
                        <Texto>
                          {option.data.distance && addCommasToNumber(option.data.distance, 1)} Est. {formattedDistance}
                        </Texto>
                      </Horizontal>
                      <Vertical flex={1} alignItems='end'>
                        {option.data.split_me && <BBDTag style={{ width: 'fit-content' }}>Split Me</BBDTag>}
                      </Vertical>
                    </Horizontal>
                  )}
                />
              </Form.Item>
            </DataItem>
          ) : (
            !!store?.split_partners?.length && (
              <Vertical verticalCenter className='full-height-width px-2'>
                <Texto className='mr-4' appearance='medium' weight='bold'>
                  Split Partners
                </Texto>
                <Horizontal className='pt-1'>
                  <Tooltip
                    placement='topRight'
                    title={showEllipsis ? store?.split_partners?.map((partner) => partner.site_number).join(', ') : ''}
                  >
                    <Button
                      onMouseEnter={() =>
                        setShowEllipsis(textBoxRef?.current?.offsetWidth < textBoxRef?.current?.scrollWidth)
                      }
                      className='ghost-gravi-button cursor-default full-height-width'
                    >
                      <p ref={textBoxRef} className='text-truncate' style={{ width: '100%', textAlign: 'left' }}>
                        {store?.split_partners.map((partner, i) => (
                          <BBDTag key={i} theme2 className='mr-2' style={{ minWidth: 'fit-content' }}>
                            <LinkOutlined className='mr-2' />
                            {partner.site_number}
                          </BBDTag>
                        ))}
                      </p>
                    </Button>
                  </Tooltip>
                </Horizontal>
              </Vertical>
            )
          )}
          {editing && (
            <Horizontal className='mt-4' verticalCenter>
              <Horizontal flex={1} justifyContent='start'>
                <Popconfirm
                  title={`Are you sure you want to delete Store # ${store.store_number}?`}
                  onConfirm={(e) => {
                    e.preventDefault()
                    handleDelete(store.store_id)
                  }}
                  okText='Yes'
                  cancelText='No'
                >
                  <GraviButton
                    style={{
                      backgroundColor: 'transparent',
                      color: 'var(--theme-error)',
                      border: '1px solid var(--theme-error)',
                    }}
                    buttonText='Delete Site'
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </Horizontal>
              <Horizontal flex={1} justifyContent='end'>
                <GraviButton onClick={() => setEditing(false)} buttonText='Cancel' data-cy='cancel-btn' />

                <GraviButton
                  icon={isSaveLoading && <LoadingOutlined />}
                  loading={isSaveLoading}
                  buttonText='Save Changes'
                  htmlType='submit'
                  theme2
                  className='ml-3'
                  data-cy='save-changes-btn'
                />
              </Horizontal>
            </Horizontal>
          )}
        </div>
      </Form>
    </div>
  )
}

function StoreNameNumberRow({
  store,
  canEditStores,
  canEditCreditHold,
  editing,
  setEditing,
  handleDelete,
  counterparties,
  saveLoading,
  monitorStrategies,
  wrapperRefs,
}) {
  const tankHoldCount = store?.tanks?.filter((tank) => tank.compliance_hold).length
  function Header() {
    const wrapperRef = useRef(null)
    if (!store) return null
    const isWarning =
      store.monitoring_strategy?.toLowerCase() === 'unmonitored' ||
      store.monitoring_strategy === 'No Readings (Call-In)' ||
      store.monitoring_strategy === 'Manual Tank Readings'
    const icon =
      store?.monitoring_strategy?.toLowerCase() === 'unmonitored' ? (
        <DisconnectOutlined className='pr-3' />
      ) : store.monitoring_strategy === 'No Readings (Call-In)' ? (
        <PhoneOutlined className='pr-3' />
      ) : store.monitoring_strategy === 'Manual Tank Readings' ? (
        <EditOutlined className='pr-3' />
      ) : (
        <DownloadOutlined className='pr-3' />
      )

    return (
      <div className='flex items-center mb-4'>
        <div className='flex-1 flex items-center' data-cy='site-details-heading'>
          <Texto className='mr-3' category='heading'>
            SITE DETAILS
          </Texto>
          {!editing && (
            <>
              <BBDTag
                textTransform='capitalize'
                className='store-status-tag'
                theme2={store.status?.toLowerCase() === 'open' && !store?.credit_hold}
                theme3={store.status?.toLowerCase() === 'closed'}
                error={store?.credit_hold || store?.status_override === 'comp. hold'}
              >
                {store?.credit_hold && <LockFilled className='mr-2' />}
                {store.credit_hold ? 'Credit Hold' : store.status_override}
              </BBDTag>
              {store?.monitoring_strategy && (
                <BBDTag textTransform='capitalize' className='store-status-tag' warning={isWarning} theme2={!isWarning}>
                  {icon}
                  {store.monitoring_strategy}
                </BBDTag>
              )}
              <BBDTag textTransform='capitalize' className='store-status-tag' theme2>
                {store.drop_workflow || 'Sticking'}
              </BBDTag>
              {store.hide_tank_alerts && (
                <Tooltip title='Tank monitoring alerts are hidden for this site'>
                  <BBDTag className='store-status-tag' warning>
                    Alerts Hidden
                  </BBDTag>
                </Tooltip>
              )}
              {tankHoldCount > 0 ? (
                <Tooltip title={`${tankHoldCount} ${tankHoldCount === 1 ? `tank` : `tanks`} with Hold`}>
                  <Link to={`/admin/tank/?store_number=${store.store_number}`} style={{ color: 'var(--theme-error)' }}>
                    {store.tank_count} Tanks
                  </Link>
                </Tooltip>
              ) : (
                <Link to={`/admin/tank/?store_number=${store.store_number}`}>{store.tank_count} Tanks</Link>
              )}
            </>
          )}
          {editing && (
            <div ref={wrapperRefs.monitor_type}>
              <Form.Item required name='monitoring_strategy'>
                <Select
                  style={{ width: 200 }}
                  placeholder='Monitor Type'
                  getPopupContainer={(_) => wrapperRefs.monitor_type?.current}
                >
                  {monitorStrategies?.map((strategy, i) => (
                    <Option key={i} value={strategy}>
                      {strategy}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          )}
          {editing && (
            <div className='ml-2' ref={wrapperRefs.drop_workflow}>
              <Form.Item required name='drop_workflow' initialValue={store.drop_workflow || 'Sticking'}>
                <Select style={{ width: 100 }} getPopupContainer={(_) => wrapperRefs.drop_workflow?.current}>
                  <Option key='Sticking' value='Sticking'>
                    Sticking
                  </Option>
                  <Option key='No Sticking' value='No Sticking'>
                    No Sticking
                  </Option>
                </Select>
              </Form.Item>
            </div>
          )}
          {editing && (
            <div className='ml-2 flex items-center'>
              <Tooltip title='Hide tank monitoring alerts (keeps hold flags)'>
                <Form.Item
                  name='hide_tank_alerts'
                  valuePropName='checked'
                  initialValue={store.hide_tank_alerts}
                  className='mb-0'
                >
                  <Switch />
                </Form.Item>
              </Tooltip>
              <span className='ml-2'>Hide Tank Alerts</span>
            </div>
          )}
        </div>
        {canEditStores && !editing && (
          <div className='flex items-center'>
            <EditSaveButton
              editing={editing}
              onEdit={() => {
                setEditing(true)
              }}
            />
          </div>
        )}
      </div>
    )
  }
  return (
    <>
      <Header />
      {editing ? (
        <Row gutter={15}>
          <Col span={6}>
            <DataItem label='Site Number'>
              <FormInput
                name='store_number'
                message='Site Number is required'
                placeholder='Site Number'
                required
                editable={editing}
                initialValue={store.store_number}
              />
            </DataItem>
          </Col>
          <Col span={6}>
            <DataItem label='Site Name'>
              <FormInput
                name='name'
                message='Site Name is required'
                placeholder='Site Name'
                required
                editable={editing}
                initialValue={store.name}
              />
            </DataItem>
          </Col>
          <Col span={6}>
            <DataItem label='Status'>
              <DropdownSelect
                editable={editing}
                name='status'
                placeholder='Site Status'
                initialValue={store.status}
                options={['open', 'closed']}
              />
            </DataItem>
          </Col>
          {canEditCreditHold && (
            <Col span={6}>
              <DataItem label='Credit Hold'>
                <Form.Item key='credit_hold' name='credit_hold'>
                  <Switch name='credit_hold' defaultChecked={store.credit_hold} className='error-switch ml-3' />
                </Form.Item>
              </DataItem>
            </Col>
          )}
        </Row>
      ) : (
        <div className='flex items-center'>
          <div className='vertical-flex-center flex-1'>
            <Texto className='mr-4' appearance='medium' weight='bold'>
              Store Number
            </Texto>
            <Texto className='mr-4' category='h4'>
              {store.store_number}
            </Texto>
          </div>
          <div className='vertical-flex-center flex-1'>
            <Texto className='mr-4' appearance='medium' weight='bold'>
              Store Name
            </Texto>
            <Texto className='mr-4' category='h4'>
              {store.name}
            </Texto>
          </div>
        </div>
      )}
    </>
  )
}
